import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { ArrowRight, Lock, CheckCircle2, Circle, BookOpen, Award } from "lucide-react";

export default function Dashboard() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({ completed_lesson_ids: [], total_lessons: 0 });
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/");
      return;
    }
    api.get("/course").then(({ data }) => setCourse(data));
    if (user.enrolled) {
      api.get("/progress").then(({ data }) => setProgress(data));
    }
  }, [user, loading, navigate]);

  const handleBuy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/create", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      setBuying(false);
    }
  };

  if (loading || !user || !course) {
    return (
      <div className="min-h-screen bg-[#F7F6F0]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Loading…</div>
        </div>
      </div>
    );
  }

  const totalLessons = progress.total_lessons || course.modules.reduce((a, m) => a + m.lessons.length, 0);
  const completedCount = progress.completed_lesson_ids.length;
  const pct = totalLessons ? Math.round((completedCount / totalLessons) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F7F6F0]" data-testid="dashboard">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Welcome back, {user.name?.split(" ")[0]}</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#121614] leading-tight">
              {user.enrolled ? "Pick up where you left off." : "You're one step from enrolment."}
            </h1>
          </div>
          <div className="lg:col-span-4">
            {user.enrolled ? (
              <div className="card-flat p-6" data-testid="progress-card">
                <div className="overline mb-3">Your progress</div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-serif-display text-4xl text-[#1A3626]">{completedCount}</span>
                  <span className="text-[#4B534E]">/ {totalLessons} lessons</span>
                </div>
                <div className="h-1.5 bg-[#EFEBE1] w-full overflow-hidden">
                  <div className="h-full bg-[#C56B52] transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="font-mono-plex text-xs uppercase tracking-wider text-[#4B534E] mt-2">{pct}% complete</div>
                {pct === 100 && (
                  <button
                    onClick={() => navigate("/certificate")}
                    className="btn-accent w-full justify-center mt-5 text-sm py-2 px-4"
                    data-testid="view-certificate-btn"
                  >
                    <Award size={14} /> View your certificate
                  </button>
                )}
              </div>
            ) : (
              <div className="card-flat p-6" data-testid="enroll-card">
                <div className="overline mb-3">Locked</div>
                <p className="text-sm text-[#4B534E] mb-5">Enrol once to unlock all 12 modules, lifetime.</p>
                <button onClick={handleBuy} disabled={buying} className="btn-accent w-full justify-center" data-testid="dashboard-enroll-btn">
                  {buying ? "Loading..." : "Enrol — $119"}
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* MODULES LIST */}
        <div className="border border-[#E0DCD1] bg-white" data-testid="modules-list">
          {course.modules.map((m, mi) => {
            const completedInModule = m.lessons.filter(l => progress.completed_lesson_ids.includes(l.id)).length;
            return (
              <div key={m.id} className={`${mi !== course.modules.length - 1 ? "border-b border-[#E0DCD1]" : ""}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 lg:p-8">
                  <div className="md:col-span-3">
                    <div className="font-mono-plex text-xs text-[#C56B52]">MODULE {m.number}</div>
                    <div className="font-mono-plex text-xs text-[#4B534E] mt-1">
                      {user.enrolled ? `${completedInModule}/${m.lessons.length}` : `${m.lessons.length} lessons`}
                    </div>
                  </div>
                  <div className="md:col-span-6">
                    <h3 className="font-serif-display text-2xl text-[#121614] leading-tight mb-2">{m.title}</h3>
                    <p className="text-sm text-[#4B534E]">{m.summary}</p>
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-start">
                    {user.enrolled ? (
                      <button
                        onClick={() => navigate(`/learn/${m.lessons[0].id}`)}
                        className="btn-secondary text-sm py-2 px-4"
                        data-testid={`open-module-${m.id}`}
                      >
                        <BookOpen size={14} /> Open
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-[#4B534E] text-sm">
                        <Lock size={14} /> Locked
                      </div>
                    )}
                  </div>
                </div>

                {user.enrolled && (
                  <div className="bg-[#F7F6F0] border-t border-[#E0DCD1] px-6 lg:px-8 py-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {m.lessons.map((l) => {
                        const done = progress.completed_lesson_ids.includes(l.id);
                        return (
                          <button
                            key={l.id}
                            onClick={() => navigate(`/learn/${l.id}`)}
                            className="flex items-center gap-2 text-sm text-[#4B534E] hover:text-[#1A3626] py-1 text-left"
                            data-testid={`lesson-link-${l.id}`}
                          >
                            {done ? <CheckCircle2 size={14} className="text-[#1A3626]" /> : <Circle size={14} />}
                            <span className="truncate">{l.title}</span>
                            <span className="font-mono-plex text-[10px] text-[#4B534E] ml-auto">{l.duration}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
